import { useEffect, useState } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import Landing from "./components/Landing";
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
//import Dashboard from "./components/Dashboard";
import DashboardAutoLogout from "./LogoutTimeOut/DashboardAutoLogout";
import SelectionComponent from "./components/SelectionComponent";
import LocalLoanApproveStep from "./components/LocalLoanApproveStep";
import ScholarshipsPage from "./components/scholarships/ScholarshipsPage";
import ReviewScholarshipsPage from "./components/scholarships/review/ReviewScholarshipsPage";
import ScholarshipReviewPage from "./components/scholarships/review/ScholarshipReviewPage";
import ProgrammeApplicantsPage from "./components/scholarships/review/ProgrammeApplicantsPage";
import StudentPortalPage, { AcademicProfilePage, MyApplicationsPage } from "./components/scholarships/student/StudentPortalPage";
import StudentScholarshipPage from "./components/scholarships/student/StudentScholarshipPage";
import ApplicationPage from "./components/scholarships/student/ApplicationPage";
import { EvaluatorApplicationsPage, MARKING_GROUPS, MarkingGroupPage, MarkingRedirect } from "./components/scholarships/evaluator/EvaluatorPages";
import { PartnerScholarshipPage, PartnerScholarshipsPage } from "./components/scholarships/cooperation/PartnerPages";
import SponsorsPage from "./components/scholarships/sponsors/SponsorsPage";
import ScholarshipReportPage from "./components/scholarships/reports/ScholarshipReportPage";
import SettingsPage from "./components/scholarships/settings/SettingsPage";
import { LoanApprovalPage, NominationsPage } from "./components/scholarships/loans/LoanPages";
import { HliListPage, HliListsPage } from "./components/privateLoans/HliListsPages";
import { BrdPrivateListsPage, PrivateListPage, PrivateListsPage } from "./components/privateLoans/StaffListsPages";
import { LoanPaymentPage, LoanPaymentsPage } from "./components/scholarships/brd/BrdPages";
import { HliPublicLoansPage, StaffPublicLoansPage } from "./components/scholarships/publicLoans/PublicLoanPages";
import { InstitutionBursariesPage, InstitutionBursaryPage } from "./components/bursaries/InstitutionBursariesPages";
import AbroadCallsPage from "./components/abroadLoans/AbroadCallsPage";
import AbroadProgrammesPage from "./components/abroadLoans/ProgrammesPage";
import AbroadStudentCallPage from "./components/abroadLoans/StudentCallPage";
import AbroadApplicationPage from "./components/abroadLoans/AbroadApplicationPage";
import AbroadReviewCallsPage from "./components/abroadLoans/ReviewCallsPage";
import AbroadCallReviewPage from "./components/abroadLoans/CallReviewPage";
import { AbroadApprovalsPage, AbroadCallApprovalsPage } from "./components/abroadLoans/ApprovalsPages";
import { ABROAD_LOANS } from "./components/abroadLoans/awards";
import MarkingPage from "./components/scholarships/evaluator/MarkingPage";
import { AbroadEvaluatorApplicationsPage, AbroadMarkingPage } from "./components/abroadLoans/EvaluatorPages";
import StudentClaimsList from "./components/StudentClaimsList";
import AdminUserManagement from "./components/AdminUserManagement";
import HliAdminAutoLogout from "./LogoutTimeOut/HliAdminAutoLogout";
import HliBursaryApplications from "./components/HliBursaryApplications";
import HliSelectionComponent from "./components/HliSelectionComponent";
import HliGrantedStudents from "./components/HliGrantedStudents";
import HliStudentClaims from "./components/HliStudentClaims";
import StudentLocalLoan from "./components/StudentLocalLoan";
import GrantedStudents from "./components/BursaryPlan/GrantedStudents";
import { isAuthenticated, hasAnyRole } from './utils/auth';
import AuthCallback from "./components/AuthCallback";
import { rememberReturnPath } from "./auth/returnPath";
import { isKeycloakEnabled, restoreSession } from "./auth/keycloak";
import { ROLES, homePath } from './auth/roles';
import DashboardLayout from "./layouts/DashboardLayout";
import { Loading } from "./components/ui";

// One client for the app's lifetime, so the query cache survives re-renders
const queryClient = new QueryClient();

function App() {
  // Pick up an existing Keycloak session after a page reload, and refresh the
  // access token if it expired while the tab was closed. Until this resolves
  // the app behaves as if signed out, which is the safe default.
  const [sessionChecked, setSessionChecked] = useState(!isKeycloakEnabled());

  useEffect(() => {
    if (sessionChecked) return;
    let cancelled = false;
    restoreSession()
      .catch((error) => console.warn("could not restore session:", error))
      .finally(() => {
        if (!cancelled) setSessionChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionChecked]);

  //authentication

  const AuthenticationRouter = () => {
    const location = useLocation();
    if (!isAuthenticated()) {
      // Come back here after login, e.g. to a private scholarship's shared link
      rememberReturnPath(location.pathname + location.search);
      return <Navigate to="/" />;
    }
    
    return <Outlet />;
  };

  // Opens the page only for callers holding one of the given roles — the same roles the
  // backend requires for that page's endpoints. UI only: the backend checks every request.
  // A signed-in caller without the role goes to their own home page, not back to login;
  // to "/" only when that home is the page they were refused, so it cannot loop.
  const RoleBasedRoute = ({ Component, roles, props }) => {
    const location = useLocation();
    if (hasAnyRole(...roles)) return <Component {...props} />;
    const home = homePath();
    if (home === location.pathname) {
      console.warn(`No access to ${location.pathname} for this role; returning to sign-in`);
      return <Navigate to="/" replace />;
    }
    return <Navigate to={home} replace />;
  };

  if (!sessionChecked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Loading />
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route element={<AuthenticationRouter />}>
          <Route element={<DashboardLayout />}>
            {/* Legacy bursary pages, now part of the institution and selection pages */}
            <Route path="/localLoan" element={<Navigate to="/loanApprovalStep" replace />} />
            <Route path="/busaryapplication" element={<Navigate to="/bursaries/institutions" replace />} />
            <Route path="/uploadBursaryApplication" element={<Navigate to="/local-loan-results" replace />} />
            <Route path="/GrantedStudents"
            element={
                <RoleBasedRoute
                  Component={GrantedStudents}
                  roles={[ROLES.ADMIN]}
                />
              }
             />
            <Route
              path="/selection"
              element={
                <RoleBasedRoute
                  Component={SelectionComponent}
                  roles={[ROLES.ADMIN]}
                />
              }
            />
            <Route
              path="/loanApprovalStep"
              element={
                <RoleBasedRoute
                  Component={LocalLoanApproveStep}
                  roles={[ROLES.ADMIN]}
                />
              }
            />
            <Route
              path="/dashboard"
              element={
                <RoleBasedRoute
                  Component={DashboardAutoLogout}
                  roles={[ROLES.ADMIN]}
                />
              }
            />
            <Route
              path="/applicantsList"
              element={
                <RoleBasedRoute
                  Component={ReviewScholarshipsPage}
                  roles={[ROLES.ADMIN, ROLES.DG, ROLES.MINISTER]}
                />
              }
            />
            <Route
              path="/applicantsList/:scholarshipId"
              element={
                <RoleBasedRoute
                  Component={ScholarshipReviewPage}
                  roles={[ROLES.ADMIN, ROLES.DG, ROLES.MINISTER]}
                />
              }
            />
            <Route
              path="/applicantsList/:scholarshipId/programmes/:programmeId"
              element={
                <RoleBasedRoute
                  Component={ProgrammeApplicantsPage}
                  roles={[ROLES.ADMIN, ROLES.DG, ROLES.MINISTER]}
                />
              }
            />
            <Route
              path="/my-applications"
              element={
                <RoleBasedRoute
                  Component={MyApplicationsPage}
                  roles={[ROLES.APPLICANT]}
                />
              }
            />
            <Route
              path="/academic-profile"
              element={
                <RoleBasedRoute
                  Component={AcademicProfilePage}
                  roles={[ROLES.APPLICANT]}
                />
              }
            />
            <Route
              path="/studentPortal"
              element={
                <RoleBasedRoute
                  Component={StudentPortalPage}
                  roles={[ROLES.APPLICANT]}
                />
              }
            />
            {/* A scholarship's page for students; private scholarships are reached only by this link */}
            <Route
              path="/scholarships/:scholarshipId"
              element={
                <RoleBasedRoute
                  Component={StudentScholarshipPage}
                  roles={[ROLES.APPLICANT]}
                />
              }
            />
            <Route
              path="/my-applications/:applicationId"
              element={
                <RoleBasedRoute
                  Component={ApplicationPage}
                  roles={[ROLES.APPLICANT]}
                />
              }
            />
            {/* A student's abroad loan call and application, on the abroad-loans API */}
            <Route path="/loans/abroad/calls/:callId" element={<RoleBasedRoute Component={AbroadStudentCallPage} roles={[ROLES.APPLICANT]} />} />
            <Route path="/my-applications/abroad/:applicationId" element={<RoleBasedRoute Component={AbroadApplicationPage} roles={[ROLES.APPLICANT]} />} />
            {/* The Minister's former home: abroad loans now have their own approvals */}
            <Route path="/loan-approvals" element={<Navigate to="/loans/abroad/approvals" replace />} />
            {/* Sponsored scholarships' nominations, signed off by the DG */}
            <Route
              path="/nominations"
              element={
                <RoleBasedRoute
                  Component={NominationsPage}
                  roles={[ROLES.ADMIN, ROLES.DG]}
                />
              }
            />
            <Route
              path="/nominations/:scholarshipId"
              element={
                <RoleBasedRoute
                  Component={LoanApprovalPage}
                  roles={[ROLES.ADMIN, ROLES.DG]}
                />
              }
            />
            {/* Abroad loan calls and their programmes run on the Loans service abroad-loans API */}
            <Route
              path="/loans/abroad"
              element={<RoleBasedRoute Component={AbroadCallsPage} roles={[ROLES.ADMIN, ROLES.DG, ROLES.MINISTER]} />}
            />
            <Route
              path="/loans/abroad/programmes"
              element={<RoleBasedRoute Component={AbroadProgrammesPage} roles={[ROLES.ADMIN, ROLES.DG, ROLES.MINISTER]} />}
            />
            <Route
              path="/loans/abroad/applicants"
              element={<RoleBasedRoute Component={AbroadReviewCallsPage} roles={[ROLES.ADMIN, ROLES.DG, ROLES.MINISTER]} />}
            />
            <Route
              path="/loans/abroad/applicants/:callId"
              element={<RoleBasedRoute Component={AbroadCallReviewPage} roles={[ROLES.ADMIN, ROLES.DG, ROLES.MINISTER]} />}
            />
            <Route
              path="/loans/abroad/approvals"
              element={<RoleBasedRoute Component={AbroadApprovalsPage} roles={[ROLES.ADMIN, ROLES.DG, ROLES.MINISTER]} />}
            />
            <Route
              path="/loans/abroad/approvals/:callId"
              element={<RoleBasedRoute Component={AbroadCallApprovalsPage} roles={[ROLES.ADMIN, ROLES.DG, ROLES.MINISTER]} />}
            />
            {/* Private institutions' lists of students, decided as a whole (Loans service hli-lists API) */}
            <Route
              path="/loans/private"
              element={<RoleBasedRoute Component={PrivateListsPage} roles={[ROLES.ADMIN, ROLES.DG, ROLES.MINISTER]} />}
            />
            <Route
              path="/loans/private/:listId"
              element={<RoleBasedRoute Component={PrivateListPage} roles={[ROLES.ADMIN, ROLES.DG, ROLES.MINISTER]} />}
            />
            <Route path="/brd/private-lists" element={<RoleBasedRoute Component={BrdPrivateListsPage} roles={[ROLES.BRD]} />} />
            <Route
              path="/brd/private-lists/:listId"
              element={<RoleBasedRoute Component={PrivateListPage} roles={[ROLES.BRD]} props={{ basePath: "/brd/private-lists" }} />}
            />
            <Route path="/hli/lists" element={<RoleBasedRoute Component={HliListsPage} roles={[ROLES.HLI_ADMIN]} />} />
            <Route path="/hli/lists/:listId" element={<RoleBasedRoute Component={HliListPage} roles={[ROLES.HLI_ADMIN]} />} />
              }
            />
            <Route
              path="/local-loan-results"
              element={
                <RoleBasedRoute
                  Component={StaffPublicLoansPage}
                  roles={[ROLES.ADMIN, ROLES.DG, ROLES.MINISTER]}
                />
              }
            />
            <Route path="/local-loan-results/*" element={<Navigate to="/local-loan-results" replace />} />
            <Route
              path="/bursaries/institutions"
              element={
                <RoleBasedRoute
                  Component={InstitutionBursariesPage}
                  roles={[ROLES.ADMIN]}
                />
              }
            />
            <Route
              path="/bursaries/institutions/:institutionId"
              element={
                <RoleBasedRoute
                  Component={InstitutionBursaryPage}
                  roles={[ROLES.ADMIN]}
                />
              }
            />
            <Route
              path="/loan-payments"
              element={
                <RoleBasedRoute
                  Component={LoanPaymentsPage}
                  roles={[ROLES.BRD]}
                />
              }
            />
            <Route
              path="/loan-payments/:applicationId"
              element={
                <RoleBasedRoute
                  Component={LoanPaymentPage}
                  roles={[ROLES.BRD]}
                />
              }
            />
            <Route
              path="/loan-payments/abroad/:applicationId"
              element={<RoleBasedRoute Component={LoanPaymentPage} roles={[ROLES.BRD]} props={{ source: ABROAD_LOANS }} />}
            />
            <Route
              path="/sponsors"
              element={
                <RoleBasedRoute
                  Component={SponsorsPage}
                  roles={[ROLES.ADMIN, ROLES.DG, ROLES.MINISTER]}
                />
              }
            />
            <Route
              path="/partner"
              element={
                <RoleBasedRoute
                  Component={PartnerScholarshipsPage}
                  roles={[ROLES.SCHOLARSHIP_OWNER]}
                />
              }
            />
            <Route
              path="/partner/:scholarshipId"
              element={
                <RoleBasedRoute
                  Component={PartnerScholarshipPage}
                  roles={[ROLES.SCHOLARSHIP_OWNER]}
                />
              }
            />
            <Route
              path="/evaluation"
              element={
                <RoleBasedRoute
                  Component={MarkingRedirect}
                  roles={[ROLES.EVALUATOR]}
                />
              }
            />
            {MARKING_GROUPS.map((g) => (
              <Route
                key={g.value}
                path={`/evaluation/${g.value}`}
                element={<RoleBasedRoute Component={MarkingGroupPage} props={{ group: g.value }} roles={[ROLES.EVALUATOR]} />}
              />
            ))}
            <Route
              path="/evaluation/:scholarshipId"
              element={
                <RoleBasedRoute
                  Component={EvaluatorApplicationsPage}
                  roles={[ROLES.EVALUATOR]}
                />
              }
            />
            <Route
              path="/evaluation/applications/:applicationId"
              element={
                <RoleBasedRoute
                  Component={MarkingPage}
                  roles={[ROLES.EVALUATOR]}
                />
              }
            />
            <Route
              path="/evaluation/loans/:callId"
              element={<RoleBasedRoute Component={AbroadEvaluatorApplicationsPage} roles={[ROLES.EVALUATOR]} />}
            />
            <Route
              path="/evaluation/loans/applications/:applicationId"
              element={<RoleBasedRoute Component={AbroadMarkingPage} roles={[ROLES.EVALUATOR]} />}
            />
            <Route
              path="/student/localLoan"
              element={
                <RoleBasedRoute Component={StudentLocalLoan} roles={[ROLES.APPLICANT]} />
              }
            />
            <Route
              path="/scholarshipList"
              element={
                <RoleBasedRoute
                  Component={ScholarshipsPage}
                  roles={[ROLES.ADMIN, ROLES.DG, ROLES.MINISTER]}
                />
              }
            />
            <Route
              path="/settings"
              element={
                <RoleBasedRoute
                  Component={SettingsPage}
                  roles={[ROLES.ADMIN]}
                />
              }
            />
            <Route
              path="/reports/scholarships"
              element={
                <RoleBasedRoute
                  Component={ScholarshipReportPage}
                  roles={[ROLES.ADMIN, ROLES.DG, ROLES.MINISTER]}
                />
              }
            />
            <Route
              path="/studentClaims"
              element={
                <RoleBasedRoute
                  Component={StudentClaimsList}
                  roles={[ROLES.ADMIN]}
                />
              }
            />
            <Route
              path="/admin/users"
              element={
                <RoleBasedRoute
                  Component={AdminUserManagement}
                  roles={[ROLES.ADMIN]}
                />
              }
            />
            <Route
              path="/hliAdmin"
              element={
                <RoleBasedRoute
                  Component={HliAdminAutoLogout}
                  roles={[ROLES.HLI_ADMIN]}
                />
              }
            />
            <Route
              path="/hli/bursaryApplications"
              element={
                <RoleBasedRoute
                  Component={HliBursaryApplications}
                  roles={[ROLES.HLI_ADMIN]}
                />
              }
            />
            <Route
              path="/hli/selection"
              element={
                <RoleBasedRoute
                  Component={HliSelectionComponent}
                  roles={[ROLES.HLI_ADMIN]}
                />
              }
            />
            <Route
              path="/hli/uploadStudents"
              element={
                <RoleBasedRoute
                  Component={HliPublicLoansPage}
                  roles={[ROLES.HLI_ADMIN]}
                />
              }
            />
            {/* Uploads have no page of their own since they carry no decisions */}
            <Route path="/hli/uploadStudents/*" element={<Navigate to="/hli/uploadStudents" replace />} />
            <Route path="/hli/public-loans/*" element={<Navigate to="/hli/uploadStudents" replace />} />
            <Route
              path="/hli/grantedStudents"
              element={
                <RoleBasedRoute
                  Component={HliGrantedStudents}
                  roles={[ROLES.HLI_ADMIN]}
                />
              }
            />
            <Route
              path="/hli/studentClaims"
              element={
                <RoleBasedRoute
                  Component={HliStudentClaims}
                  roles={[ROLES.HLI_ADMIN]}
                />
              }
            />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </QueryClientProvider>
  );
}

export default App;
